/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import { apiPost } from './apiClient';
import { setAuthToken, removeAuthToken } from './apiConfig';
import API_CONFIG from './apiConfig';

/**
 * Send verification OTPs to user's email and/or phone
 * @param {Object} data - { email?: string, phone?: string }
 * @returns {Promise}
 */
export const sendVerificationOTPs = async (data) => {
  return apiPost(API_CONFIG.endpoints.sendVerificationOTPs, data, { skipAuth: true });
};

/**
 * Verify OTPs sent to email and/or phone
 * @param {Object} data - { email?: string, emailOtp?: string, phone?: string, phoneOtp?: string }
 * @returns {Promise}
 */
export const verifyOTP = async (data) => {
  return apiPost(API_CONFIG.endpoints.verifyOTP, data, { skipAuth: true });
};

/**
 * Register a new user account
 * @param {Object} data - { email: string, password: string, name: string, phone?: string, referralCode?: string }
 * @returns {Promise}
 */
export const register = async (data) => {
  const response = await apiPost(API_CONFIG.endpoints.register, data, { skipAuth: true });
  
  // Store token if provided in response
  // Token can be at response.token, response.data.token, or response.accessToken
  const token = response.token || response.data?.token || response.accessToken;
  if (token) {
    setAuthToken(token);
    console.log('Token stored successfully after registration');
  } else {
    console.warn('No token found in registration response:', response);
  }
  
  return response;
};

/**
 * Login user and get authentication token
 * @param {Object} data - { email: string, password: string } or { phone: string, password: string }
 * @returns {Promise}
 */
export const login = async (data) => {
  // Validate input
  if (!data || (!data.email && !data.phone) || !data.password) {
    throw new Error('Email/Phone and password are required');
  }

  // Prepare the login payload
  // Based on backend error "Username and password are required", the backend expects "username" field
  // But Postman collection shows "email", so we'll send username as the identifier
  const loginData = {
    username: data.email || data.phone,
    password: data.password,
  };

  // Also include email/phone for backward compatibility
  if (data.email) {
    loginData.email = data.email;
  }
  if (data.phone) {
    loginData.phone = data.phone;
  }

  console.log('Login service sending:', { ...loginData, password: '***' });
  
  const response = await apiPost(API_CONFIG.endpoints.login, loginData, { skipAuth: true });
  
  // Store token if provided in response
  // Token can be at response.token, response.data.token, or response.accessToken
  const token = response.token || response.data?.token || response.accessToken;
  if (token) {
    setAuthToken(token);
    console.log('Token stored successfully');
  } else {
    console.warn('No token found in response:', response);
  }
  
  return response;
};

/**
 * Logout user (clear all tokens and data from browser)
 */
export const logout = () => {
  // Remove auth token
  removeAuthToken();
  
  // Clear all localStorage data
  localStorage.clear();
  
  // Clear all sessionStorage data
  sessionStorage.clear();
};

